import React, { useMemo } from "react";
import "./style.css";
import {
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Modal,
    Radio,
    RadioGroup,
    Tooltip
} from "@material-ui/core";
import { renderDetalle } from "./utils";
import ItemWrapper from "./card/ItemWrapper";
import { LocalAtm } from "@material-ui/icons";
import { getV2, postV2 } from "../../api/axiosV2";
import { openAlert } from "simple-react-alert";

const ItemCompletado = props => {
    const [showRefundModal, setShowRefundModal] = React.useState(false);
    const [refundInfo, setRefundInfo] = React.useState(null);
    const [refundLoading, setRefundLoading] = React.useState(false);
    const [refundError, setRefundError] = React.useState("");
    const [refundMethod, setRefundMethod] = React.useState("");
    const [refundSubmitting, setRefundSubmitting] = React.useState(false);

    const isReembolsable = useMemo(() => 
        props.pedido.estado === "Cancelado" &&
        props.pedido.tarjeta &&
        props.pedido.pagado
    , [props.pedido])

    const getRefundOptionLabel = option => {
        if (!option) return "";
        if (option.type === "mercado_pago") return "MercadoPago";
        if (option.type === "saldo_billetera") return "Saldo Billetera";
        return option.label || option.type;
    };

    const getRefundMethodLabel = method => {
        if (!method) return "";
        if (method === "mercado_pago") return "MERCADO PAGO";
        if (method === "saldo_billetera") return "SALDO BILLETERA";
        return method;
    };

    const getPaymentLabel = payment => {
        if (!payment) return "-";
        if (payment.type === "account_money") return "Dinero en cuenta";
        return `Tarjeta (${payment.method})`;
    };

    const loadRefundInfo = async () => {
        setRefundLoading(true);
        setRefundError("");
        try {
            const response = await getV2(`/payments/refund/${props.pedido.id}`);
            setRefundInfo(response.data);
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                "Error al obtener información de reembolso";
            setRefundInfo({ success: false, error: message });
            setRefundError(message);
        } finally {
            setRefundLoading(false);
        }
    };

    const handleRefund = async () => {
        setShowRefundModal(true);
        setRefundMethod("");
        await loadRefundInfo();
    };

    const submitRefund = async () => {
        if (!refundMethod || !refundInfo?.success || refundInfo?.status !== "paid") {
            return;
        }

        setRefundSubmitting(true);
        try {
            const response = await postV2(`/payments/refund/${props.pedido.id}`, {
                method: refundMethod
            });

            if (response?.data?.success) {
                openAlert({
                    message: "Reembolso procesado con éxito",
                    type: "success"
                });
                setRefundInfo(prev => {
                    if (!prev) return prev;
                    console.log("Refund response:", response.data);
                    const isPending = response?.data?.refundInfo?.refundPending === true;
                    return {
                        ...prev,
                        status: isPending ? "paid" : "refunded",
                        refundInfo: {
                            ...(prev.refundInfo || {}),
                            ...(response?.data?.refundInfo || {})
                        }
                    };
                });
            } else {
                openAlert({
                    message: response?.data?.error || "Error al reembolsar",
                    type: "danger"
                });
            }
        } catch (error) {
            const message =
                error?.response?.data?.error || "Error al reembolsar";
            openAlert({
                message,
                type: "danger"
            });
        } finally {
            setRefundSubmitting(false);
        }
    };

    let computedClassName =
        props.pedido.estado === "Cancelado" ? "itemCancelado" : "itemCompletado";
    const isRefundPending = refundInfo?.refundPending === true;
    const refundStatusLabel = isRefundPending
        ? "En proceso"
        : refundInfo?.status === "refunded"
            ? "Reembolsado"
            : "Pagado";
    const refundStatusClass = isRefundPending
        ? "refundStatus--pending"
        : refundInfo?.status === "refunded"
            ? "refundStatus--done"
            : "refundStatus--paid";

    return (
        <ItemWrapper className={computedClassName} pedido={props.pedido}>
            {renderDetalle(props.pedido)}
            {!!isReembolsable && 
                <Tooltip title="Reembolsar pedido" placement="top">
                    <Button onClick={handleRefund} size="small">
                        <LocalAtm />
                    </Button>
                </Tooltip>
            }
            {showRefundModal && (
                <Modal
                    open={showRefundModal}
                    onClose={() => setShowRefundModal(false)}
                    className="modalContainer"
                >
                    <div className="refundModalBody">
                        <div className="refundHeader">
                            <p className="refundTitle">Reembolso</p>
                            <p className="refundSubtitle">Pedido #{props.pedido.id}</p>
                        </div>
                        {refundLoading && <CircularProgress size={24} />}
                        {!refundLoading && refundInfo?.success && (
                            <>
                                <div className="refundSummary">
                                    <div className="refundRow">
                                        <span className="refundLabel">Monto</span>
                                        <span className="refundValue">${refundInfo.total}</span>
                                    </div>
                                    <div className="refundRow">
                                        <span className="refundLabel">Pago</span>
                                        <span className="refundValue">{getPaymentLabel(refundInfo.payment)}</span>
                                    </div>
                                    <div className="refundRow">
                                        <span className="refundLabel">Estado</span>
                                        <span
                                            className={`refundStatus ${refundStatusClass}`}
                                        >
                                            {refundStatusLabel}
                                        </span>
                                    </div>
                                    {refundInfo.status === "refunded" && refundInfo.refund?.method && (
                                        <div className="refundRow">
                                            <span className="refundLabel">Destino</span>
                                            <span className="refundValue">
                                                {getRefundMethodLabel(refundInfo.refund.method)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {isRefundPending && (
                                    <div className="refundNotice">
                                        Reembolso en proceso por MercadoPago. Puede impactar en unos minutos.
                                    </div>
                                )}
                                {refundInfo.status === "paid" && !isRefundPending && (
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Método de reembolso</FormLabel>
                                        <RadioGroup
                                            value={refundMethod}
                                            onChange={e => setRefundMethod(e.target.value)}
                                        >
                                            {(refundInfo.refundOptions || []).map(option => (
                                                <FormControlLabel
                                                    key={option.type}
                                                    value={option.type}
                                                    control={<Radio />}
                                                    label={getRefundOptionLabel(option)}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                )}
                                {refundInfo.status === "paid" && !isRefundPending && (
                                    <Button
                                        variant="outlined"
                                        className="btnVerificar"
                                        onClick={submitRefund}
                                        disabled={refundSubmitting || !refundMethod}
                                        color="secondary"
                                    >
                                        {refundSubmitting ? "Reembolsando..." : "Reembolsar"}
                                    </Button>
                                )}
                                {refundInfo.status === "paid" && !isRefundPending && !refundMethod && (
                                    <p className="refundDisabledHint">Seleccioná un método para habilitar el reembolso.</p>
                                )}
                            </>
                        )}
                        {!refundLoading && refundError && (
                            <p>{refundError}</p>
                        )}
                    </div>
                </Modal>
            )}
        </ItemWrapper>
    );
};

export default ItemCompletado;
